import {
	getNearestSlide,
	getSlideScrollLeft,
	type SlideBox,
} from "@ext/markdown/elements/gallery/logic/carouselScroll";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

export const getCarouselItems = (frame: HTMLElement | null) =>
	frame?.querySelector<HTMLElement>("[data-gallery-items]") ?? null;

const readSlides = (items: HTMLElement): SlideBox[] =>
	Array.from(items.querySelectorAll<HTMLElement>(":scope > * > *"), (slide) => ({
		left: slide.offsetLeft,
		width: slide.offsetWidth,
	}));

const useCarouselPosition = (frameRef: RefObject<HTMLElement>) => {
	const [index, setIndex] = useState(0);
	const targetRef = useRef<number | null>(null);

	useEffect(() => {
		const items = getCarouselItems(frameRef.current);
		if (!items) return;

		let frame = 0;
		const onScroll = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => {
				const nearest = getNearestSlide(readSlides(items), items.scrollLeft, items.clientWidth);
				if (nearest === targetRef.current) targetRef.current = null;
				setIndex(nearest);
			});
		};

		items.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			cancelAnimationFrame(frame);
			items.removeEventListener("scroll", onScroll);
		};
	}, [frameRef]);

	const step = useCallback(
		(direction: 1 | -1) => {
			const items = getCarouselItems(frameRef.current);
			if (!items) return;
			const slides = readSlides(items);
			const from = targetRef.current ?? getNearestSlide(slides, items.scrollLeft, items.clientWidth);
			const target = Math.min(Math.max(from + direction, 0), slides.length - 1);
			targetRef.current = target;
			items.scrollTo({ left: getSlideScrollLeft(slides[target], items.clientWidth), behavior: "smooth" });
		},
		[frameRef],
	);

	return { index, step };
};

export default useCarouselPosition;
